<template>
  <transition name="modal">
    <div v-if="show" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" @click.self="closeEditor">
      <div class="bg-white rounded-lg shadow-xl max-w-6xl w-full transform transition-all max-h-[90vh] overflow-hidden flex flex-col">
        <!-- Header -->
        <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 class="text-lg font-semibold text-gray-900">
            {{ isCreating ? 'Create Classifier' : 'Edit Classifier' }}
          </h3>
          <button
            @click="closeEditor"
            class="text-gray-400 hover:text-gray-500"
          >
            <FontAwesomeIcon :icon="['fas', 'times']" class="h-6 w-6" />
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto p-6">
          <div class="space-y-6">
            <!-- Basic Info Section -->
            <div class="bg-gray-50 rounded-lg p-4">
              <h4 class="text-sm font-semibold text-gray-900 mb-4">Basic Information</h4>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Classifier Name <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.name"
                    type="text"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent"
                    placeholder="e.g., VBC Readiness"
                  >
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Target Entity Type <span class="text-red-500">*</span>
                  </label>
                  <select
                    v-model="form.target_entity_type"
                    :disabled="!isCreating"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent disabled:bg-gray-100"
                  >
                    <option value="">Select entity type...</option>
                    <option v-for="entityType in entityTypes" :key="entityType.id" :value="entityType.type_name">
                      {{ entityType.type_name }}
                    </option>
                  </select>
                  <p v-if="!isCreating" class="text-xs text-gray-500 mt-1">Cannot change target type after creation</p>
                </div>

                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Color
                  </label>
                  <div class="flex items-center space-x-2">
                    <input
                      v-model="form.color"
                      type="color"
                      class="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                    >
                    <span class="text-sm text-gray-600">{{ form.color }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Classification Prompt Section -->
            <div class="bg-gray-50 rounded-lg p-4">
              <div class="flex justify-between items-center mb-4">
                <h4 class="text-sm font-semibold text-gray-900">Classification Prompt</h4>
                <button
                  @click="showPromptHelp = !showPromptHelp"
                  class="text-xs text-blue-600 hover:text-blue-700"
                >
                  <FontAwesomeIcon :icon="['fas', 'circle-question']" class="mr-1" />
                  {{ showPromptHelp ? 'Hide' : 'Show' }} Help
                </button>
              </div>

              <div v-if="showPromptHelp" class="mb-3 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-gray-700">
                <p class="font-medium mb-2">Tips for effective classification prompts:</p>
                <ul class="list-disc list-inside space-y-1 text-xs">
                  <li>Be specific about what data should inform the classification</li>
                  <li>Explain the criteria for each classification category</li>
                  <li>Reference the data sources that will be available</li>
                  <li>Guide the LLM to select the most appropriate tag</li>
                  <li>Include instructions about confidence scoring</li>
                </ul>
              </div>

              <textarea
                v-model="form.classification_prompt"
                rows="8"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent font-mono text-sm"
                placeholder="Enter the classification prompt that will guide the AI in classifying entities..."
              ></textarea>
              <p class="text-xs text-gray-500 mt-1">
                {{ form.classification_prompt.length }} characters
              </p>
            </div>

            <!-- InfoTags Section -->
            <div class="bg-gray-50 rounded-lg p-4">
              <div class="flex justify-between items-center mb-4">
                <h4 class="text-sm font-semibold text-gray-900">
                  Classification Tags ({{ infoTags.length }})
                </h4>
                <button
                  @click="addNewTag"
                  class="btn-primary-sm"
                >
                  <FontAwesomeIcon :icon="['fas', 'plus']" />
                  Add Tag
                </button>
              </div>

              <p class="text-xs text-gray-600 mb-3">
                Define the categories that entities can be classified into. Each tag should be distinct and mutually exclusive.
              </p>

              <!-- Tag List -->
              <div class="space-y-2">
                <div
                  v-for="(tag, index) in infoTags"
                  :key="tag.tempId || tag.id"
                  class="bg-white border border-gray-200 rounded-lg p-3"
                >
                  <div class="flex items-start space-x-3">
                    <div class="shrink-0 pt-2">
                      <div
                        class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium text-white"
                        :style="{ backgroundColor: tag.UIColor }"
                      >
                        {{ index + 1 }}
                      </div>
                    </div>

                    <div class="flex-1 space-y-2">
                      <input
                        v-model="tag.name"
                        type="text"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent"
                        placeholder="Tag name (e.g., 'VBC Ready')"
                      >
                      <textarea
                        v-model="tag.description"
                        rows="2"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent text-sm"
                        placeholder="Description of when this tag applies..."
                      ></textarea>
                      <div class="flex items-center space-x-2">
                        <label class="text-xs text-gray-600">Color:</label>
                        <input
                          v-model="tag.UIColor"
                          type="color"
                          class="h-8 w-16 rounded border border-gray-300 cursor-pointer"
                        >
                      </div>
                    </div>

                    <div class="shrink-0 flex flex-col space-y-1">
                      <button
                        @click="moveTag(index, -1)"
                        :disabled="index === 0"
                        class="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                        title="Move up"
                      >
                        <FontAwesomeIcon :icon="['fas', 'arrow-up']" class="text-xs" />
                      </button>
                      <button
                        @click="moveTag(index, 1)"
                        :disabled="index === infoTags.length - 1"
                        class="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                        title="Move down"
                      >
                        <FontAwesomeIcon :icon="['fas', 'arrow-down']" class="text-xs" />
                      </button>
                      <button
                        @click="deleteTag(index)"
                        class="p-1.5 text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        <FontAwesomeIcon :icon="['fas', 'trash']" />
                      </button>
                    </div>
                  </div>
                </div>

                <div v-if="infoTags.length === 0" class="text-center py-8 text-gray-500">
                  <FontAwesomeIcon :icon="['fas', 'inbox']" class="text-4xl mb-2" />
                  <p class="text-sm">No tags defined. Add at least 3 classification tags.</p>
                </div>
              </div>
            </div>

            <!-- Data Sources Section -->
            <div class="bg-gray-50 rounded-lg p-4">
              <h4 class="text-sm font-semibold text-gray-900 mb-4">Data Sources</h4>

              <p class="text-xs text-gray-600 mb-3">
                Select which performance data should be included when classifying entities.
              </p>

              <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
                <label
                  v-for="source in availableDataSources"
                  :key="source.key"
                  class="flex items-start space-x-2 p-3 border border-gray-200 rounded-lg hover:bg-white cursor-pointer transition-colors"
                >
                  <input
                    v-model="form.performance_data_sources"
                    type="checkbox"
                    :value="source.key"
                    class="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-dark"
                  >
                  <div>
                    <div class="text-sm font-medium text-gray-900">{{ source.label }}</div>
                    <div class="text-xs text-gray-500">{{ source.description }}</div>
                  </div>
                </label>
              </div>
            </div>

            <!-- Context InfoTagTypes Section -->
            <div class="bg-gray-50 rounded-lg p-4">
              <h4 class="text-sm font-semibold text-gray-900 mb-4">Context Classifiers (Optional)</h4>

              <p class="text-xs text-gray-600 mb-3">
                When classifying an entity, include its existing classifications from other classifiers as additional context.
                This can help create more nuanced classifications based on multiple dimensions.
              </p>

              <div class="space-y-2">
                <label
                  v-for="classifier in availableContextClassifiers"
                  :key="classifier.id"
                  class="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-white cursor-pointer transition-colors"
                >
                  <input
                    v-model="form.include_infotag_types"
                    type="checkbox"
                    :value="classifier.id"
                    class="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-dark"
                  >
                  <div class="flex-1">
                    <div class="text-sm font-medium text-gray-900">{{ classifier.name }}</div>
                    <div class="text-xs text-gray-500">Target: {{ classifier.target_entity_type }}</div>
                  </div>
                </label>

                <div v-if="availableContextClassifiers.length === 0" class="text-center py-4 text-gray-500 text-sm">
                  No other classifiers available
                </div>
              </div>
            </div>

            <!-- Settings Section -->
            <div class="bg-gray-50 rounded-lg p-4">
              <h4 class="text-sm font-semibold text-gray-900 mb-4">Settings</h4>

              <div class="space-y-3">
                <label class="flex items-start space-x-3">
                  <input
                    v-model="form.enable_web_search"
                    type="checkbox"
                    class="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-dark"
                  >
                  <div>
                    <div class="text-sm font-medium text-gray-900">Enable Web Search</div>
                    <div class="text-xs text-gray-500">
                      Allow the AI to search the web for additional context when classifying entities.
                      This may increase classification time and cost.
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div>
            <!-- Validation Errors -->
            <div v-if="validationErrors.length > 0" class="mb-3">
              <div class="bg-red-50 border border-red-200 rounded-lg p-3">
                <div class="flex items-start space-x-2 mb-2">
                  <FontAwesomeIcon :icon="['fas', 'exclamation-circle']" class="text-red-600 mt-0.5" />
                  <div class="flex-1">
                    <p class="text-sm font-medium text-red-800 mb-2">Please fix the following errors:</p>
                    <ul class="space-y-1">
                      <li
                        v-for="(error, index) in validationErrors"
                        :key="index"
                        class="text-sm text-red-700 flex items-start"
                      >
                        <span class="mr-2">•</span>
                        <span>{{ error }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <!-- Success indicators -->
            <div v-else-if="form.name && form.target_entity_type && form.classification_prompt && infoTags.length >= 3" class="mb-3">
              <div class="flex items-center space-x-2 text-sm text-green-600">
                <FontAwesomeIcon :icon="['fas', 'check-circle']" />
                <span>All required fields are valid</span>
              </div>
            </div>
          </div>

          <div class="flex justify-end space-x-3 mt-3">
            <button
              @click="closeEditor"
              class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              @click="saveClassifier"
              :disabled="!canSave || saving"
              class="btn-primary"
            >
              <FontAwesomeIcon v-if="saving" :icon="['fas', 'spinner']" class="animate-spin" />
              {{ saving ? 'Saving...' : (isCreating ? 'Create Classifier' : 'Save Changes') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'ClassifierEditor',
  components: {
    FontAwesomeIcon
  },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    classifier: {
      type: Object,
      default: null
    }
  },
  emits: ['close', 'saved'],
  data() {
    return {
      isCreating: false,
      saving: false,
      showPromptHelp: false,

      // Form data
      form: {
        name: '',
        color: '#3B82F6',
        target_entity_type: '',
        classification_prompt: '',
        performance_data_sources: [],
        include_infotag_types: [],
        enable_web_search: false
      },

      // InfoTags
      infoTags: [],
      nextTempId: 1,

      // Available options
      entityTypes: [],
      availableContextClassifiers: [],
      availableDataSources: [
        {
          key: 'cost_risk',
          label: 'Cost & Risk',
          description: 'PMPM costs, total costs, attribution'
        },
        {
          key: 'awv',
          label: 'AWV Rates',
          description: 'Annual Wellness Visit completion'
        },
        {
          key: 'coding',
          label: 'Coding',
          description: 'HCC coding and RAF scores'
        },
        {
          key: 'attribution',
          label: 'Attribution',
          description: 'Patient attribution by payer'
        },
        {
          key: 'utilization',
          label: 'Utilization',
          description: 'Claims-based utilization metrics'
        },
        {
          key: 'vbc',
          label: 'VBC Programs',
          description: 'Value-based care participation'
        },
        {
          key: 'providers',
          label: 'Provider Roster',
          description: 'Provider count and specialty distribution'
        }
      ],
      validationErrors: []
    };
  },
  computed: {
    canSave() {
      return this.form.name &&
             this.form.target_entity_type &&
             this.form.classification_prompt &&
             this.infoTags.length >= 3 &&
             this.validationErrors.length === 0;
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.initialize();
      }
    },
    form: {
      handler() {
        this.validate();
      },
      deep: true
    },
    infoTags: {
      handler() {
        this.validate();
      },
      deep: true
    }
  },
  methods: {
    async initialize() {
      this.isCreating = !this.classifier;

      if (this.isCreating) {
        this.resetForm();
      } else {
        await this.loadClassifier();
      }

      await this.fetchEntityTypes();
      await this.fetchContextClassifiers();
    },

    resetForm() {
      this.form = {
        name: '',
        color: this.generateRandomColor(),
        target_entity_type: '',
        classification_prompt: '',
        performance_data_sources: [],
        include_infotag_types: [],
        enable_web_search: false
      };
      this.infoTags = [];
      this.nextTempId = 1;
    },

    async loadClassifier() {
      // Load form data from classifier
      const config = this.classifier.metadata?.classification_config || {};

      this.form = {
        name: this.classifier.name || '',
        color: this.classifier.UIColor || '#3B82F6',
        target_entity_type: config.target_entity_type || '',
        classification_prompt: config.classification_prompt || '',
        performance_data_sources: config.performance_data_sources || [],
        include_infotag_types: config.include_infotag_types || [],
        enable_web_search: config.enable_web_search || false
      };

      // Load existing InfoTags
      await this.fetchInfoTags();
    },

    async fetchEntityTypes() {
      try {
        const response = await axios.get('/api/entity-types/');
        this.entityTypes = response.data;
      } catch (error) {
        console.error('Error fetching entity types:', error);
      }
    },

    async fetchContextClassifiers() {
      try {
        const response = await axios.get('/api/infotagtypes/');
        // Filter to classifiers with classification_config, excluding current one
        this.availableContextClassifiers = response.data.filter(itt =>
          itt.metadata?.classification_config &&
          (!this.classifier || itt.id !== this.classifier.id)
        ).map(itt => ({
          id: itt.id,
          name: itt.name,
          target_entity_type: itt.metadata.classification_config.target_entity_type
        }));
      } catch (error) {
        console.error('Error fetching context classifiers:', error);
      }
    },

    async fetchInfoTags() {
      if (!this.classifier) return;

      try {
        const response = await axios.get('/api/infotags/', {
          params: { infoTagType_id: this.classifier.id }
        });
        this.infoTags = response.data;
      } catch (error) {
        console.error('Error fetching InfoTags:', error);
        this.infoTags = [];
      }
    },

    addNewTag() {
      this.infoTags.push({
        tempId: this.nextTempId++,
        name: '',
        description: '',
        UIColor: this.generateRandomColor(),
        content_type_model: 'entity'
      });
    },

    deleteTag(index) {
      if (confirm('Delete this tag? This cannot be undone.')) {
        this.infoTags.splice(index, 1);
      }
    },

    moveTag(index, direction) {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= this.infoTags.length) return;

      const temp = this.infoTags[index];
      this.infoTags[index] = this.infoTags[targetIndex];
      this.infoTags[targetIndex] = temp;
    },

    validate() {
      this.validationErrors = [];

      if (!this.form.name) {
        this.validationErrors.push('Classifier name is required');
      }

      if (!this.form.target_entity_type) {
        this.validationErrors.push('Target entity type is required');
      }

      if (!this.form.classification_prompt) {
        this.validationErrors.push('Classification prompt is required');
      } else if (this.form.classification_prompt.length < 100) {
        this.validationErrors.push('Classification prompt should be at least 100 characters');
      }

      if (this.infoTags.length < 3) {
        this.validationErrors.push('At least 3 classification tags are required');
      }

      // Check for empty tag names
      const emptyTags = this.infoTags.filter(tag => !tag.name);
      if (emptyTags.length > 0) {
        this.validationErrors.push(`${emptyTags.length} tag(s) missing names`);
      }

      // Check for empty tag descriptions
      const shortDescriptions = this.infoTags.filter(tag => !tag.description || tag.description.length < 20);
      if (shortDescriptions.length > 0) {
        this.validationErrors.push(`${shortDescriptions.length} tag(s) have short/missing descriptions`);
      }
    },

    async saveClassifier() {
      this.validate();
      if (this.validationErrors.length > 0) {
        alert('Please fix validation errors before saving');
        return;
      }

      this.saving = true;

      try {
        // Build classification config
        const classificationConfig = {
          target_entity_type: this.form.target_entity_type,
          classification_prompt: this.form.classification_prompt,
          performance_data_sources: this.form.performance_data_sources,
          include_infotag_types: this.form.include_infotag_types,
          enable_web_search: this.form.enable_web_search
        };

        // Step 1: Create or update InfoTagType
        let infoTagTypeId;

        if (this.isCreating) {
          const response = await axios.post('/api/infotagtypes/', {
            name: this.form.name,
            UIColor: this.form.color,
            metadata: {
              classification_config: classificationConfig
            }
          });
          infoTagTypeId = response.data.id;
        } else {
          await axios.patch(`/api/infotagtypes/${this.classifier.id}/`, {
            name: this.form.name,
            UIColor: this.form.color,
            metadata: {
              classification_config: classificationConfig
            }
          });
          infoTagTypeId = this.classifier.id;
        }

        // Step 2: Handle InfoTags
        // Delete removed tags (existing tags not in current list)
        if (!this.isCreating) {
          const existingIds = this.infoTags.filter(t => t.id).map(t => t.id);
          const response = await axios.get('/api/infotags/', {
            params: { infoTagType_id: infoTagTypeId }
          });
          const oldTags = response.data;

          for (const oldTag of oldTags) {
            if (!existingIds.includes(oldTag.id)) {
              await axios.delete(`/api/infotags/${oldTag.id}/`);
            }
          }
        }

        // Create or update tags
        for (const tag of this.infoTags) {
          const tagData = {
            infoTagType: infoTagTypeId,
            name: tag.name,
            description: tag.description,
            UIColor: tag.UIColor,
            content_type_model: 'entity'
          };

          if (tag.id) {
            // Update existing tag
            await axios.patch(`/api/infotags/${tag.id}/`, tagData);
          } else {
            // Create new tag
            await axios.post('/api/infotags/', tagData);
          }
        }

        this.$emit('saved');
        this.closeEditor();
      } catch (error) {
        console.error('Error saving classifier:', error);
        alert('Failed to save classifier. Please try again.');
      } finally {
        this.saving = false;
      }
    },

    closeEditor() {
      this.resetForm();
      this.$emit('close');
    },

    generateRandomColor() {
      const colors = [
        '#3B82F6', '#8B5CF6', '#EC4899', '#10B981', '#F59E0B',
        '#6366F1', '#14B8A6', '#F43F5E', '#06B6D4', '#84CC16'
      ];
      return colors[Math.floor(Math.random() * colors.length)];
    }
  }
};
</script>

<style scoped>
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-active .transform,
.modal-leave-active .transform {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-enter-from {
  opacity: 0;
}

.modal-enter-from .transform,
.modal-leave-to .transform {
  transform: scale(0.95);
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Scrollbar styling */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>
